import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { authActions } from '../../redux/store';
import { toast } from 'react-hot-toast';
import Header from '../components/Header';
import axiosInstance from '../../axiosInstance';
import Footer from '../components/Footer';

const UserSignup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const checkUserToken = () => {
      const usertoken = localStorage.getItem('token');
      if (usertoken) {
        navigate('/');
        toast.success('Welcome back');
      }
    };
    checkUserToken();
  }, [navigate]);

  // ----- MULTI-STEP STATE -----
  const [step, setStep] = useState(1);

  // All form data in one place
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    password: '',
    referralCode: '',
    bio: '',
    phone: '',
    birthdate: '' // yyyy-mm-dd from <input type="date">
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // ---- STEP VALIDATION ----
  const validateStep = (s) => {
    switch (s) {
      case 1:
        if (!form.firstName?.trim()) return 'First name is required';
        if (!form.email?.trim()) return 'Email is required';
        if (!form.password?.trim()) return 'Password is required';
        return null;
      case 2:
        if (!form.phone?.trim()) return 'Phone number is required';
        return null;
      case 3:
        if (!form.birthdate?.trim()) return 'Birthdate is required';
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) {
      toast.error(err);
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => setStep(prev => Math.max(1, prev - 1));

  // ---- FINAL SUBMIT (Step 3) ----
  const handleSubmitFinal = async (e) => {
    e.preventDefault();
    const err = validateStep(3);
    if (err) return toast.error(err);

    try {
      // Build a payload you expect at the API
      const payload = {
        firstName: form.firstName,
        email: form.email,
        password: form.password,
        referralCode: form.referralCode || undefined,
        bio: form.bio || undefined,
        phone: form.phone,
        birthdate: form.birthdate
      };

      const { data } = await axiosInstance.post('/signup', payload); // <-- update to your real endpoint
      const { success, user, token, message } = data;

      if (success) {
        // Save
        localStorage.setItem('token', token || user?.token);
        localStorage.setItem('userId', user?._id);

        dispatch(authActions.login());
        toast.success('Account created successfully');
        navigate('/');
      } else {
        toast.error(message || 'Signup failed');
      }
    } catch (error) {
      console.error('Signup error', error);
      toast.error(error?.response?.data?.message || 'Something went wrong');
    }
  };

  // ---- SCREENS ----
  const Step1 = (
    <div className="card shadow-lg rounded-4 border-0 p-4 mx-auto" style={{ maxWidth: 400 }}>
      <div className="card-body text-center">
        <h5 className="fw-bold mb-2">Can You elaborate on your identity? 😎</h5>
        <p className="text-muted small mb-4">
          It will Display on your Profile and you will not able to change it later
        </p>

        <div className="mb-3">
          <input
            type="text"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            className="form-control rounded-3"
            placeholder="First Name"
          />
        </div>
        <div className="mb-3">
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="form-control rounded-3"
            placeholder="Email"
          />
        </div>
        <div className="mb-3 position-relative">
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            className="form-control rounded-3 pe-5"
            placeholder="Password"
          />
          <span className="position-absolute top-50 end-0 translate-middle-y me-3">
            <i className="bi bi-eye" />
          </span>
        </div>
        <div className="mb-3">
          <input
            type="text"
            name="referralCode"
            value={form.referralCode}
            onChange={handleChange}
            className="form-control rounded-3"
            placeholder="Referral Code (optional)"
          />
        </div>
        <div className="mb-4">
          <textarea
            name="bio"
            value={form.bio}
            onChange={handleChange}
            className="form-control rounded-3"
            placeholder="Bio (optional)"
          />
        </div>

        <div className="d-flex gap-2">
          <button type="button" onClick={handleNext} className="btn btn-primary w-100 rounded-3 fw-semibold">
            Next  <i className="ri-arrow-right-s-line" /> 
          </button>
        </div>
      </div>
    </div>
  );

  const Step2 = (
    <div className="mx-auto bg-white shadow-lg rounded-4 p-4 p-md-5" style={{ maxWidth: 400 }}>
      <h5 className="fw-bold mb-2 text-center">Your GoMeet identity 😎</h5>
      <p className="text-muted small mb-4 text-center">
        Add your phone number and your job to tell other what you do for a living.
      </p>

      <div className="input-group input-group-md mb-4">
        <span className="input-group-text border-2">+91</span>
        <input
          type="tel"
          name="phone"
          value={form.phone}
          onChange={handleChange}
          className="form-control border-2"
          placeholder="Phone number"
        />
      </div>

      <div className="d-flex gap-2">
        <button type="button" onClick={handleBack} className="btn btn-dark w-50 rounded-3">
          <i className="ri-arrow-left-s-line" />  Back
        </button>
        <button type="button" onClick={handleNext} className="btn btn-primary w-50 rounded-3 fw-semibold">
          Next    <i className="ri-arrow-right-s-line" /> 
        </button>
      </div>
    </div>
  );

  const Step3 = (
    <div className="mx-auto bg-white shadow-lg rounded-4 p-4 p-md-5 text-center" style={{ maxWidth: 400 }}>
      <h4 className="fw-bold mb-2">Let's celebrate you 🎂</h4>
      <p className="text-muted mb-4">
        Tell us your birthdate. Your profile does not display your birthdate, only your age.
      </p>

         <div className="input-group input-group-md mb-5">
          <input
            type="date"
            name="birthdate"
            value={form.birthdate}
            onChange={handleChange}
            className="form-control border-2"
            placeholder="DD/MM/YYYY"
          />
        </div>

        <div className="d-flex gap-2">
          <button type="button" onClick={handleBack} className="btn btn-dark w-50 rounded-3">
           <i className="ri-arrow-left-s-line" />  Back
          </button>
      <button type="button" onClick={handleNext} className="btn btn-primary w-50 rounded-3 fw-semibold">
          Next    <i className="ri-arrow-right-s-line" /> 
        </button>
        </div>
     </div>
  );


    const Step4 = (
   <div
  className="mx-auto bg-white shadow-lg rounded-4 p-4 p-md-5 text-center"
  style={{ maxWidth: 480 }}
>
  <h4 className="fw-bold mb-2">Be true to yourself ✨</h4>
  <p className="text-muted mb-4">
    Choose the gender that best represents you. Authenticity is key to meaningful
    connections.
  </p>

     {/* Choices */}
    <div className="d-grid gap-3 mb-4 text-start">
      {["MALE", "FEMALE", "BOTH"].map((opt) => (
        <label
          key={opt}
          className={`border ${form.gender === opt ? "border-primary shadow-sm" : "border-2"} rounded-3 py-3 px-3 fw-semibold cursor-pointer`}
          role="button"
          aria-pressed={form.gender === opt}
        >
          <input
            type="radio"
            name="gender"
            value={opt}
            checked={form.gender === opt}
            onChange={handleChange}
            className="d-none"
          />
          {opt}
        </label>
      ))}
    </div>

    <div className="d-flex gap-2">
          <button type="button" onClick={handleBack} className="btn btn-dark w-50 rounded-3">
           <i className="ri-arrow-left-s-line" />  Back
          </button>
    <button
     onClick={handleNext}
      type="button"
      className="btn btn-primary rounded-3 w-50 "
     >
      Next <i className="ri-arrow-right-s-line ms-1" />
    </button>
     </div>
 </div>

  );

  const Step5 = (
    <div
  className="mx-auto bg-white shadow-lg rounded-4 p-4 p-md-5"
  style={{ maxWidth: 560 }}
>
  <h4 className="fw-bold mb-2">Your relationship goals 💘</h4>
  <p className="text-muted mb-4">
    Choose the type of relationship you’re seeking on Datify. Love, friendship,
    or something between—it’s your choice.
  </p>

     <div className="d-grid gap-3 mb-4">
      {[
        {
          value: "Dating",
          icon: "🧑‍❤️‍🧑",
          desc:
            "Seeking love and meaningful connections? Choose dating for genuine relationships.",
        },
        {
          value: "Friendship",
          icon: "🤝",
          desc:
            "Expand your social circle and make new friends. Opt for friendship today.",
        },
        {
          value: "Casual",
          icon: "🙂",
          desc:
            "Looking for fun and relaxed encounters? Select casual for carefree connections.",
        },
        {
          value: "Serious Relationship",
          icon: "💍",
          desc:
            "Ready for commitment and a lasting partnership? Pick serious relationship.",
        },
        {
          value: "Open to Options",
          icon: "😎",
          desc:
            "Explore various connections and keep your options open with this choice.",
        },
        {
          value: "Networking",
          icon: "🤝",
          desc:
            "Connect professionally and expand your network. Choose networking now.",
        },
        {
          value: "Exploration",
          icon: "🪐",
          desc:
            "Embark on a journey of discovery. Select exploration for new experiences.",
        },
      ].map((opt) => (
        <label
          key={opt.value}
          className={`border ${
            form.relationshipGoal === opt.value
              ? "border-primary shadow-sm"
              : "border-2"
          } rounded-3 p-3 cursor-pointer`}
          role="button"
          aria-pressed={form.relationshipGoal === opt.value}
        >
          <input
            type="radio"
            name="relationshipGoal"
            value={opt.value}
            checked={form.relationshipGoal === opt.value}
            onChange={handleChange}
            className="d-none"
          />

          <div className="fw-semibold d-flex align-items-start">
            <span className="me-2">{opt.value}</span>
            <span className="ms-auto">{opt.icon}</span>
          </div>
          <div className="text-muted small mt-1">{opt.desc}</div>
        </label>
      ))}
    </div>

   <div className="d-flex gap-2">
          <button type="button" onClick={handleBack} className="btn btn-dark w-50 rounded-3">
           <i className="ri-arrow-left-s-line" />  Back
          </button>
      <button type="button" onClick={handleNext} className="btn btn-primary w-50 rounded-3 fw-semibold">
          Next    <i className="ri-arrow-right-s-line" /> 
        </button>
        </div>

 </div>

  );


  const Step6 = (
  <div
    className="mx-auto bg-white shadow-lg rounded-4 p-4 p-md-5"
    style={{ maxWidth: 560 }}
  >
    <h5 className="fw-bold mb-2">Find matches nearby 📍</h5>
    <p className="text-muted mb-4 fs-6">
      Select your preferred distance range to discover matches conveniently.
      We'll help you find love close by.
    </p>

    {/* Distance label + live value */}
    <div className="d-flex justify-content-between align-items-center mb-1">
      <label htmlFor="distanceKm" className="fw-semibold mb-0">
        Distance Preference
      </label>
      <div className="fw-semibold">
        {Number(form.distanceKm ?? 10).toFixed(1)} km
      </div>
    </div>

    {/* Slider */}
    <input
      id="distanceKm"
      type="range"
      name="distanceKm"
      min="1"
      max="200"
      step="0.5"
      value={form.distanceKm ?? 10}
      onChange={handleChange}   // your generic handler is fine
      className="form-range accent-primary mb-4"
      aria-label="Distance Preference"
    />

    {/* Buttons */}
    <div className="d-flex gap-2">
      <button
        type="button"
        onClick={handleBack}
        className="btn btn-dark w-50 rounded-3"
      >
        <i className="ri-arrow-left-s-line" /> Back
      </button>

      <button
        type="button"
        onClick={handleNext}
        className="btn btn-primary w-50 rounded-3 fw-semibold"
      >
        Next <i className="ri-arrow-right-s-line ms-1" />
      </button>
    </div>
  </div>
);

const Step7 = (
  <div
    className="mx-auto bg-white shadow-lg rounded-4 p-4 p-md-5"
    style={{ maxWidth: 680 }}
  >
    <h5 className="fw-bold mb-2">Discover like-minded people 🤝</h5>
    <p className="text-muted mb-4 fs-6">
      Share your interests, passions, and hobbies. We’ll connect you with people
      who share your enthusiasm.
    </p>

    {/* Search */}
    <div className="position-relative mb-3">
      <i className="ri-search-line position-absolute top-50 translate-middle-y ms-3 text-muted" />
      <input
        type="text"
        className="form-control form-control-lg ps-9 rounded-4"
        placeholder="Search.."
        value={form.interestQuery ?? ""}
        onChange={(e) =>
          handleChange({
            target: { name: "interestQuery", value: e.target.value },
          })
        }
      />
    </div>

    {/* Chips */}
    <div className="d-flex flex-wrap gap-2 mb-4">
      {[
        { value: "Travel", icon: "🏞️" },
        { value: "Cooking", icon: "🍳" },
        { value: "Hiking", icon: "🥾" },
        { value: "Yoga", icon: "🧘" },
        { value: "Gaming", icon: "🎮" },
        { value: "Movies", icon: "🍿" },
        { value: "Books", icon: "📚" },
        { value: "Animals", icon: "🐾" },
        { value: "Wine", icon: "🍷" },
      ]
        .filter(({ value }) =>
          (form.interestQuery ?? "")
            .toLowerCase()
            .split(" ")
            .every((q) => value.toLowerCase().includes(q))
        )
        .map(({ value, icon }) => {
          const selected = Array.isArray(form.interests)
            ? form.interests.includes(value)
            : false;

          return (
            <button
              key={value}
              type="button"
              onClick={() =>
                handleChange({
                  target: {
                    name: "interests",
                    value: selected
                      ? form.interests.filter((x) => x !== value)
                      : [...(form.interests ?? []), value],
                  },
                })
              }
              className={`btn px-3 py-2 rounded-pill ${
                selected
                  ? "btn-primary text-white"
                  : "btn-outline-secondary text-dark"
              }`}
            >
              {value} {icon}
            </button>
          );
        })}
    </div>

    {/* Buttons */}
    <div className="d-flex gap-2">
      <button
        type="button"
        onClick={handleBack}
        className="btn btn-dark w-50 rounded-3"
      >
        <i className="ri-arrow-left-s-line" /> Back
      </button>

      <button
        type="button"
        onClick={handleNext}
        className="btn btn-primary w-50 rounded-3 fw-semibold"
        disabled={!form.interests || form.interests.length === 0}
      >
        Next <i className="ri-arrow-right-s-line ms-1" />
      </button>
    </div>
  </div>
);




    const last = (
    <div className="mx-auto bg-white shadow-lg rounded-4 p-4 p-md-5 text-center" style={{ maxWidth: 400 }}>
      <h5 className="fw-bold mb-2">Let's celebrate you 🎂</h5>
      <p className="text-muted mb-4">
        Tell us your birthdate. Your profile does not display your birthdate, only your age.
      </p>

      <form onSubmit={handleSubmitFinal}>
        <div className="input-group input-group-md mb-5">
          <input
            type="date"
            name="birthdate"
            value={form.birthdate}
            onChange={handleChange}
            className="form-control border-2"
            placeholder="DD/MM/YYYY"
          />
        </div>

        <div className="d-flex gap-2">
          <button type="button" onClick={handleBack} className="btn btn-dark w-50 rounded-3">
           <i className="ri-arrow-left-s-line" />  Back
          </button>
          <button type="submit" className="btn btn-primary w-50 rounded-3 fw-semibold">
            Create account ✓
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      <Header />
      <div className="container mt-5 d-flex justify-content-center align-items-center " style={{minHeight:'80vh'}}>
        {step === 1 && Step1}
        {step === 2 && Step2}
        {step === 3 && Step3}
          {step === 4 && Step4}
            {step === 5 && Step5}
                        {step === 6 && Step6}
                        {step === 7 && Step7}
                        {step === 8 && last}

      </div>
      <br />
      <Footer />
    </>
  );
};

export default UserSignup;
